import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  useBuyCardPackMutation,
  useBuyCardPackMutationVariables,
} from './__generated__/useBuyCardPack.graphql.ts';

const BUY_CARD_PACK_MUTATION = gql`
  mutation useBuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        gemsBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [COMMON_GEM]
        ) {
          amount
        }
      }
      pack {
        id
      }
      cards {
        slug
        ...RevealPackCardsDialog_anyCardInterface
      }
      errors {
        code
        message
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<useBuyCardPackMutation, useBuyCardPackMutationVariables>;

export const useBuyCardPack = (cardPackSlug: Nullable<string>) => {
  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();

  const { sport } = useSportContext();
  const { isIosApp, postMessage } = useIsMobileApp();
  const getInteractionContext = useGetInteractionContext();
  const [buyCardPackData, setBuyCardPackData] =
    useState<useBuyCardPackMutation['buyCardPack']>();

  const useNativeIOSPackOpening = isIosApp && !disableNativeIOSPackOpening;

  const [buyCardPack, { loading }] = useMutation(BUY_CARD_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const onPurchase = async () => {
    if (!cardPackSlug) {
      return;
    }
    const { data } = await buyCardPack({
      variables: {
        input: {
          cardPackSlug,
          sport,
          interactionContext: getInteractionContext(),
        },
        sport,
      },
    });
    setBuyCardPackData(data?.buyCardPack);
  };

  const onMobilePurchase = () => {
    if (!cardPackSlug) {
      return;
    }
    postMessage('buyCardPack', {
      packId: cardPackSlug,
    });
  };
  const packNotBought =
    buyCardPackData?.cards?.length === 0 || !buyCardPackData?.pack?.id;

  const onCloseRevealDialog = () => {
    setBuyCardPackData(undefined);
  };
  return {
    buyCardPackData,
    onPurchase,
    onMobilePurchase,
    useNativeIOSPackOpening,
    packNotBought,
    onCloseRevealDialog,
    loading,
  };
};
